import axios from 'axios'

const API_BASE = '/api'

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
})

// Ensure we have a CSRF cookie before any unsafe request
let csrfReady: Promise<void> | null = null
export function ensureCsrf(): Promise<void> {
  if (csrfReady) return csrfReady
  csrfReady = api.get('/auth/csrf/').then(() => undefined)
  return csrfReady
}

// ─── 401 handler ──────────────────────────────────────────────────────
// AuthProvider wires `onUnauth()` here at mount. When ANY API response
// comes back 401, we call it so React state flips to unauthenticated
// and the Login screen renders. Catches:
//   - mid-session role revocation (admin disabled the account)
//   - cross-tab logout (other tab cleared the cookie)
//   - server-side session expiry
//
// Excluded paths: the auth endpoints themselves. /auth/me/ returns 401
// when not logged in (that's its job during initial load — we'd loop
// forever otherwise). /auth/login/ returns 401 on bad creds (the user
// is trying to log in, don't kick them out). /auth/logout/ explicitly
// 401s if there's no session — also expected. /auth/csrf/ is anonymous.
const UNAUTH_EXEMPT_PATHS = [
  '/auth/me/',
  '/auth/login/',
  '/auth/logout/',
  '/auth/csrf/',
]
let onUnauthHandler: (() => void) | null = null
export function setUnauthHandler(fn: (() => void) | null) {
  onUnauthHandler = fn
}
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status
    const url: string = err?.config?.url || ''
    if (status === 401 && !UNAUTH_EXEMPT_PATHS.some((p) => url.endsWith(p))) {
      if (onUnauthHandler) onUnauthHandler()
    }
    return Promise.reject(err)
  },
)

export type User = {
  id: number
  username: string
  email: string
  is_staff: boolean
  is_superuser: boolean
  role: string
  client: { id: number; code: string; name: string } | null
  /** Backend-computed: true for superusers and role='studio_editor' /
   *  'studio_viewer'. Single check the UI uses to decide whether to
   *  render the Studio nav. */
  has_studio_access: boolean
  /** Set when a superuser is acting-as another user. Null otherwise. */
  acting_as?: {
    real_user_id: number
    real_username: string
    real_is_superuser: boolean
  } | null
}

export type ImpersonationTarget = {
  user_id: number
  username: string
  email: string
  role: string
  client_id: number
  client_code: string
  client_name: string
}

export async function listImpersonationTargets(): Promise<ImpersonationTarget[]> {
  const r = await api.get('/auth/impersonation-targets/')
  return r.data.targets
}

export async function setImpersonation(userId: number | null): Promise<{
  acting_as: User['acting_as']
  effective_user_id: number
  effective_username?: string
  effective_client_code?: string | null
}> {
  await ensureCsrf()
  const r = await api.post('/auth/impersonate/', { user_id: userId })
  return r.data
}

export type ClientSettings = {
  id: number
  code: string
  name: string
  default_storage_mode: 'cached' | 'on_demand'
  llm_log_retention: 'full' | 'redacted' | 'transient' | 'metadata_only'
  source_storage_overrides: Record<string, string>
  ai_auto_folder_enabled: boolean
  ai_domain_context: string
  // Free-form firm context JSON — exposed as {{client_context}} to every
  // prompt step. Read-only here; edited via /settings.
  context: Record<string, unknown>
}

export async function login(username: string, password: string): Promise<User> {
  await ensureCsrf()
  const r = await api.post('/auth/login/', { username, password })
  return r.data
}

export async function logout(): Promise<void> {
  await ensureCsrf()
  await api.post('/auth/logout/')
}

export async function me(): Promise<User | null> {
  try {
    const r = await api.get('/auth/me/')
    return r.data
  } catch {
    return null
  }
}

// ---------- Domain types ----------

export type Matter = {
  id: number
  client: number
  client_seq: number
  /** Human-readable matter code, e.g. "M-007". */
  composite_id?: string
  name: string
  matter_type: string
  status: string
  stage: string
  urgency: number
  summary: string
  /** Brisk inside-baseball list of key events (oldest→newest from AI).
   *  UI displays newest first by reversing. Added per Ken 2026-05-25. */
  bullet_summary?: string[]
  /** Optional structured table. No UI consumer yet (2026-05-25). */
  tabular_summary?: { columns?: string[]; rows?: string[][] } | Record<string, unknown>
  /** AI-narrated diff of what changed. Often null. */
  conceptual_diff?: string
  /** Legacy ranked-keywords list. Kept in sync as a mirror of
   *  unranked_keywords for back-compat. */
  keywords: string[]
  /** Smaller, party-focused keyword set. Treated as a set (no rank). */
  unranked_keywords?: string[]
  /** Full ranked keyword list (max 100). Future UI. */
  ranked_keywords?: string[]
  parties: unknown[]
  waiting_on: string
  /** Themed groupings written back from narrate_matter_history. */
  narrative_groups?: Array<{ label: string; summary: string; items: Array<{ artifact_id: number; note?: string }> }>
  /** Active (non-dismissed) folder assignments. Empty array = unfiled. */
  folder_assignments?: Array<{
    id: number
    folder: number
    folder_name: string
    assigned_by: 'user' | 'ai'
    assigned_at: string
  }>
  created_at: string
  updated_at: string
}

export type Folder = {
  id: number
  client: number
  parent_folder: number | null
  name: string
  description: string
  created_by: number | null
  created_by_ai: boolean
  last_edited_at: string | null
  last_edited_by: number | null
  last_edited_by_ai: boolean
  created_at: string
  updated_at: string
  matter_count_self?: number
  matter_count_recursive?: number
  depth: number
}

/** Tree node from /api/folders/tree/ — same shape as Folder plus children. */
export type FolderNode = Folder & {
  children: FolderNode[]
  matter_count_self: number
  matter_count_recursive: number
}

export type MatterFolderAssignment = {
  id: number
  matter: number
  folder: number
  folder_name: string
  matter_name: string
  assigned_by: 'user' | 'ai'
  assigned_by_username: string | null
  assigned_at: string
  dismissed_at: string | null
  dismissed_by_username: string | null
}

export const folders = {
  list: () => api.get<Folder[]>('/folders/').then(r => r.data),
  tree: () => api.get<{ folders: FolderNode[] }>('/folders/tree/').then(r => r.data.folders),
  detail: (id: number) => api.get<Folder>(`/folders/${id}/`).then(r => r.data),
  create: async (payload: { name: string; parent_folder?: number | null; description?: string }) => {
    await ensureCsrf()
    return api.post<Folder>('/folders/', payload).then(r => r.data)
  },
  update: async (id: number, payload: Partial<Pick<Folder, 'name' | 'parent_folder' | 'description'>>) => {
    await ensureCsrf()
    return api.patch<Folder>(`/folders/${id}/`, payload).then(r => r.data)
  },
  remove: async (id: number) => {
    await ensureCsrf()
    return api.delete(`/folders/${id}/`).then(r => r.data)
  },
  assignMatter: async (matter: number, folder: number) => {
    await ensureCsrf()
    return api.post<MatterFolderAssignment>('/matter-folders/', { matter, folder }).then(r => r.data)
  },
  dismissMatter: async (matter: number, folder: number) => {
    await ensureCsrf()
    return api.post<MatterFolderAssignment>('/matter-folders/dismiss/', { matter, folder }).then(r => r.data)
  },
  recentOps: (limit = 25) =>
    api.get<{ ops: Array<{ matter_id: number; matter_name: string; folder_id: number; folder_name: string; op: 'assigned' | 'dismissed'; at: string }> }>(`/matter-folders/recent-ops/?limit=${limit}`)
      .then(r => r.data.ops),
}

export type Artifact = {
  id: number
  client: number
  client_code: string
  client_seq: number
  composite_id: string  // {CLIENT_CODE}-{SOURCE_CODE}-{TYPE_CODE}-{client_seq:06d}
  source_type: 'gmail' | 'gdrive' | 'upload'
  source_external_id: string
  artifact_type: 'email' | 'document' | 'attachment'
  mime_type: string
  subject: string
  from_name: string
  from_email: string
  source_date: string | null
  visibility: 'shared' | 'restricted'
  parent_artifact: number | null
  matter_ids: number[]
  children_count: { total: number; visible: number }
  ignored: boolean
  ignored_reason: string
  // Cross-mailbox dedup. is_duplicate_of points to the primary; null
  // for primaries themselves. duplicate_count is the inverse — how
  // many other mailbox copies are linked to this row (only non-zero
  // for primaries).
  is_duplicate_of: number | null
  duplicate_count: number
  // Promoted AI fields (canonical state, written back from CR after each
  // pipeline step). Useful for list rendering without a detail fetch.
  is_personal: boolean | null
  urgency: number | null
  doc_kind: string
  is_sensitive: boolean | null
  ai_action: string
  ai_summary: string
  // email_structural step output (L2). Null for non-emails / not-yet-run.
  email_structural: {
    is_forward: boolean
    copy_in: boolean
    strictly_logistical: boolean
    is_interlineated: boolean
    legal_office_only: boolean
    is_digest: boolean
    multiple_issues_detected: boolean
    issues: string[]
  } | null
  created_at: string
}

export type ArtifactDetail = Artifact & {
  cached_content: string
  body_html: string
  cached_file_url: string | null
  cached_file_size: number | null
  comments: Array<Record<string, unknown>>
  redlines: Array<Record<string, unknown>>
  to_emails: string[]
  cc_emails: string[]
  source_modified_at: string | null
  last_seen_at: string | null
  source_status: string
  content_hash: string
  content_storage_mode: 'cached' | 'on_demand'
  message_id_header: string
  duplicates: Array<{ id: number; source_account_email: string }>
  matter_assignments: Array<{ matter: number; assigned_by: 'ai' | 'user'; confidence: number | null; assigned_at: string }>
}

export type PromptSlot = {
  id: number
  name: string
  description: string
  level: number
  output_schema: Record<string, unknown>
  /** True for slots that exist for prompt iteration only (no
   *  ComputationStep references them; never auto-fires; runs go to
   *  SandboxRun, not LlmCallLog). */
  is_sandbox: boolean
  version_count: number
  production_version_id: number | null
  created_at: string
  updated_at: string
}

export type PromptVersion = {
  id: number
  slot: number
  slot_name: string
  version: number
  content: string
  model: string
  labels: string[]
  commit_message: string
  created_by: number | null
  created_by_username: string | null
  created_at: string
  var_warnings: string[]
}

export type ComputationStep = {
  id: number
  name: string
  slot: number
  slot_name: string
  level: number
  prompt_version_label: string
  backend: string
  fanout_kind: 'none' | 'per_matter' | 'per_open_matter' | 'matter_only'
  parent_ids: number[]
  child_ids: number[]
  created_at: string
}

export type ComputationRun = {
  id: number
  artifact: number
  triggered_by: string
  started_at: string
  completed_at: string | null
  status: string
  notes: string
}

export type ComputationResult = {
  id: number
  artifact: number
  step: number
  step_name: string
  run: number | null
  prompt_version: number
  prompt_version_number: number
  output: Record<string, unknown>
  status: string
  is_stale: boolean
  error_message: string
  computed_at: string
  updated_at: string
  latency_ms: number
}

export type LlmCallLog = {
  id: number
  client: number
  artifact: number | null
  step: number | null
  prompt_version: number | null
  model: string
  artifact_subject: string
  artifact_composite_id: string
  artifact_type: string
  artifact_from_email: string
  step_name: string
  input: string
  output: string
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: string
  error_message: string
  /** Prompt-render issues caught at template-substitution time (not LLM
   *  errors). Empty array when the prompt rendered cleanly. */
  render_issues: string[]
  created_at: string
}

export type AuditLog = {
  id: number
  user: number | null
  user_username: string | null
  action: string
  target_type: string
  target_id: string
  old_value: Record<string, unknown> | null
  new_value: Record<string, unknown> | null
  note: string
  created_at: string
}

type Page<T> = { count: number; next: string | null; previous: string | null; results: T[] }

const list =
  <T,>(path: string) =>
  async (params: Record<string, string | number | boolean> = {}): Promise<T[]> => {
    const r = await api.get<Page<T>>(path, { params })
    return r.data.results
  }

const detail =
  <T,>(path: string) =>
  async (id: number | string): Promise<T> => {
    const r = await api.get<T>(`${path}${id}/`)
    return r.data
  }

export const matters = {
  list: list<Matter>('/matters/'),
  detail: detail<Matter>('/matters/'),
  create: async (payload: Partial<Matter>): Promise<Matter> => {
    await ensureCsrf()
    const r = await api.post('/matters/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<Matter>): Promise<Matter> => {
    await ensureCsrf()
    const r = await api.patch(`/matters/${id}/`, payload)
    return r.data
  },
}
export type ArtifactPage = {
  count: number
  next: string | null
  previous: string | null
  results: Artifact[]
}
export const artifacts = {
  list: list<Artifact>('/artifacts/'),
  detail: detail<ArtifactDetail>('/artifacts/'),
  // Returns the full paginated page (count + next/previous links + results),
  // unlike list() which strips to just results[].
  page: async (params: Record<string, string | number | boolean | undefined> = {}): Promise<ArtifactPage> => {
    const cleaned: Record<string, string | number | boolean> = {}
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') cleaned[k] = v
    const r = await api.get<ArtifactPage>('/artifacts/', { params: cleaned })
    return r.data
  },
  ignore: async (ids: number[], reason = 'manual'): Promise<{ updated: number; rejected: number }> => {
    await ensureCsrf()
    const r = await api.post('/artifacts/ignore/', { artifact_ids: ids, reason })
    return r.data
  },
  unignore: async (ids: number[]): Promise<{ updated: number; rejected: number }> => {
    await ensureCsrf()
    const r = await api.post('/artifacts/unignore/', { artifact_ids: ids })
    return r.data
  },
}
export const promptSlots = {
  list: list<PromptSlot>('/prompt-slots/'),
  detail: detail<PromptSlot>('/prompt-slots/'),
  vars: async (slotId: number): Promise<{ vars: Record<string, string> }> => {
    const r = await api.get(`/prompt-slots/${slotId}/vars/`)
    return r.data
  },
  /** Create a new prompt slot. Currently used for sandbox slots; production
   *  slots are still seeded via the seed_real_dag management command. */
  create: async (payload: Partial<PromptSlot>): Promise<PromptSlot> => {
    await ensureCsrf()
    const r = await api.post('/prompt-slots/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<PromptSlot>): Promise<PromptSlot> => {
    await ensureCsrf()
    const r = await api.patch(`/prompt-slots/${id}/`, payload)
    return r.data
  },
  remove: async (id: number): Promise<void> => {
    await ensureCsrf()
    await api.delete(`/prompt-slots/${id}/`)
  },
}
export const promptVersions = {
  list: list<PromptVersion>('/prompt-versions/'),
  detail: detail<PromptVersion>('/prompt-versions/'),
  bySlot: (slotId: number) => list<PromptVersion>('/prompt-versions/')({ slot: slotId }),
  // Studio Test Run uses this to surface sandbox-slot versions as
  // cross-slot pin options for any production step. Returns versions
  // whose slot has is_sandbox=True.
  sandboxOnly: () => list<PromptVersion>('/prompt-versions/')({ is_sandbox: true }),
  create: async (payload: Partial<PromptVersion>): Promise<PromptVersion> => {
    await ensureCsrf()
    const r = await api.post('/prompt-versions/', payload)
    return r.data
  },
  diff: async (ids: number[]): Promise<DiffResponse> => {
    const r = await api.get('/prompt-versions/diff/', { params: { ids: ids.join(',') } })
    return r.data
  },
}

export type PromptTestSet = {
  id: number
  client: number
  slot: number
  slot_name: string
  name: string
  description: string
  artifacts: number[]
  artifact_count: number
  expected_outputs: Record<string, unknown>
  created_by: number | null
  created_at: string
  updated_at: string
}

export const promptTestSets = {
  list: list<PromptTestSet>('/prompt-test-sets/'),
  detail: detail<PromptTestSet>('/prompt-test-sets/'),
  bySlot: (slotId: number) => list<PromptTestSet>('/prompt-test-sets/')({ slot: slotId }),
  create: async (payload: { slot: number; name: string; description?: string; artifacts?: number[]; expected_outputs?: Record<string, unknown> }): Promise<PromptTestSet> => {
    await ensureCsrf()
    const r = await api.post('/prompt-test-sets/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<{ name: string; description: string; artifacts: number[]; expected_outputs: Record<string, unknown> }>): Promise<PromptTestSet> => {
    await ensureCsrf()
    const r = await api.patch(`/prompt-test-sets/${id}/`, payload)
    return r.data
  },
  remove: async (id: number) => {
    await ensureCsrf()
    await api.delete(`/prompt-test-sets/${id}/`)
  },
}

export type TestSetRunResult = {
  artifact_id: number
  artifact_subject: string
  rendered_input: string
  output_text: string
  output_parsed: unknown
  input_tokens: number
  output_tokens: number
  latency_ms: number
  expected: unknown
  matches_expected: boolean | null
}

export async function runTestSet(versionId: number, testSetId: number): Promise<{ test_set: string; prompt_version_id: number; results: TestSetRunResult[] }> {
  await ensureCsrf()
  const r = await api.post(`/prompt-versions/${versionId}/run-test-set/`, { test_set: testSetId })
  return r.data
}
export const steps = {
  list: list<ComputationStep>('/steps/'),
  detail: detail<ComputationStep>('/steps/'),
  create: async (payload: Partial<ComputationStep>): Promise<ComputationStep> => {
    await ensureCsrf()
    const r = await api.post('/steps/', payload)
    return r.data
  },
  update: async (id: number, payload: Partial<ComputationStep>): Promise<ComputationStep> => {
    await ensureCsrf()
    const r = await api.patch(`/steps/${id}/`, payload)
    return r.data
  },
  remove: async (id: number) => {
    await ensureCsrf()
    await api.delete(`/steps/${id}/`)
  },
}
export const stepDeps = {
  list: list<{ id: number; parent: number; child: number }>('/step-dependencies/'),
  create: async (parent: number, child: number): Promise<{ id: number }> => {
    await ensureCsrf()
    const r = await api.post('/step-dependencies/', { parent, child })
    return r.data
  },
  remove: async (id: number) => {
    await ensureCsrf()
    await api.delete(`/step-dependencies/${id}/`)
  },
}
export const runs = { list: list<ComputationRun>('/runs/') }
export const results = { list: list<ComputationResult>('/results/') }
export const llmCalls = {
  list: list<LlmCallLog>('/llm-calls/'),
  /** Returns the full paginated envelope (count + results) so a caller
   *  can render Prev/Next pagers. The plain `.list()` returns just
   *  results[] for callers that don't care about pagination. */
  listPaged: async (params: Record<string, string | number> = {}): Promise<{ count: number; results: LlmCallLog[] }> => {
    const r = await api.get<{ count: number; results: LlmCallLog[] }>('/llm-calls/', { params })
    return { count: r.data.count, results: r.data.results }
  },
}

export type SandboxRun = {
  id: number
  slot: number
  slot_name: string
  prompt_version: number | null
  prompt_version_number: number | null
  artifact: number
  artifact_subject: string
  artifact_composite_id: string | null
  test_set_run: number | null
  source: 'dry_run' | 'test_set' | 'pipeline_test' | 'sandbox_slot'
  rendered_input: string
  output_text: string
  output_parsed: unknown
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: 'success' | 'error' | 'timeout'
  error_message: string
  created_by: number | null
  created_by_username: string | null
  created_at: string
}

export const sandboxRuns = {
  list: list<SandboxRun>('/sandbox-runs/'),
}

export type TestSetRun = {
  id: number
  test_set: number
  test_set_name: string
  prompt_version: number | null
  prompt_version_number: number | null
  started_at: string
  completed_at: string | null
  status: 'running' | 'completed' | 'partial' | 'failed'
  artifact_count: number
  success_count: number
  error_count: number
  notes: string
  created_by: number | null
  created_by_username: string | null
}

export const testSetRuns = {
  list: list<TestSetRun>('/test-set-runs/'),
}

export type ManualEditTargetType = 'matter' | 'artifact'

export type ManualEditEntry = {
  id: number
  client: number | null
  user: number | null
  user_username: string | null
  target_type: ManualEditTargetType
  target_id: number
  target_label: string
  field: string
  old_value: unknown
  new_value: unknown
  reason: string
  created_at: string
}

/** Save a user edit to an AI-generated field. Server applies the change
 *  to the canonical row AND records a ManualEdit audit row. Returns the
 *  persisted value + the new audit row's id. */
export async function manualEdit(payload: {
  target_type: ManualEditTargetType
  target_id: number
  field: string
  value: unknown
  reason?: string
}): Promise<{
  manual_edit_id: number
  target_type: ManualEditTargetType
  target_id: number
  field: string
  value: unknown
  detail?: string
}> {
  await ensureCsrf()
  const r = await api.post('/manual-edit/', payload)
  return r.data
}

export const manualEdits = {
  list: list<ManualEditEntry>('/manual-edits/'),
}
export const auditLog = { list: list<AuditLog>('/audit-log/') }
export const clients = {
  list: list<ClientSettings>('/clients/'),
  detail: detail<ClientSettings>('/clients/'),
  update: async (id: number, payload: Partial<ClientSettings>): Promise<ClientSettings> => {
    await ensureCsrf()
    const r = await api.patch(`/clients/${id}/`, payload)
    return r.data
  },
}

export type DiffWordOp = {
  line_a: number
  line_b: number
  a_words: string[]
  b_words: string[]
  opcodes: [string, number, number, number, number][]
}

export type DiffEntry = {
  base_version_id: number
  other_version_id: number
  lines_a: string[]
  lines_b: string[]
  opcodes: [string, number, number, number, number][]
  word_ops: DiffWordOp[]
  unified: string
}

export type DiffResponse = {
  versions: { id: number; version: number; slot: string; labels: string[]; commit_message: string; content: string }[]
  diffs: DiffEntry[]
}

export async function dryRunPrompt(opts: {
  prompt_version_id: number
  artifact_id: number
  mock_upstream?: Record<string, unknown>
}): Promise<{
  prompt_version: { id: number; version: number; slot: string; content: string }
  artifact: { id: number; subject: string; client_seq: number }
  rendered_input: string
  output_text: string
  output_parsed: unknown
  input_tokens: number
  output_tokens: number
  latency_ms: number
  model: string
}> {
  await ensureCsrf()
  const r = await api.post('/dry-run-prompt/', opts)
  return r.data
}

export async function promotePromptVersion(versionId: number, label = 'production'): Promise<{ outdated_count?: number; stale_count?: number }> {
  // Server returns `outdated_count` post-decoupling; older builds may
  // still ship `stale_count`. Caller renders whichever is present.
  await ensureCsrf()
  const r = await api.post('/promote-prompt/', { version_id: versionId, label })
  return r.data
}

// ─── Outdated / stale counts and the deliberate-stale flow ──────────
//
// See docs/specs/outdated-vs-stale.md. "Outdated" = result was computed
// under a non-current prompt version (derived). "Stale" = explicitly
// queued for rerun (DB flag, only set deliberately).

export type StepStaleCount = {
  step_id: number
  step_name: string
  slot_name: string
  level: number
  production_version: number | null
  production_version_id: number | null
  computation_result: { total: number; stale: number; outdated: number }
  matter_step_result: { total: number; stale: number; outdated: number }
}

export async function fetchStepOutdatedCounts(clientCode?: string): Promise<{ steps: StepStaleCount[]; client_code: string | null }> {
  const params = clientCode ? `?client_code=${encodeURIComponent(clientCode)}` : ''
  const r = await api.get(`/steps/outdated-counts/${params}`)
  return r.data
}

export type MarkStaleRequest = {
  client_code: string
  step_names: string[]
  matter_ids?: number[]
  artifact_ids?: number[]
  source_date_from?: string
  source_date_to?: string
  outdated_only?: boolean
  dry_run?: boolean
}

export type MarkStaleResponse = {
  counts: { computation_result: number; matter_step_result: number }
  to_flip: { computation_result: number; matter_step_result: number }
  by_step: { step: string; cr_to_flip: number; msr_to_flip: number }[]
  flipped: { computation_result: number; matter_step_result: number } | null
}

export async function markResultsStale(req: MarkStaleRequest): Promise<MarkStaleResponse> {
  await ensureCsrf()
  const r = await api.post('/results/mark-stale/', req)
  return r.data
}

// Per-artifact step drift summary, used by Studio > Artifacts to render
// a "v3→v4" chip on a row when any of its step results were computed
// under a non-current prompt version.
export type ArtifactDriftSummary = {
  computation_result: { total: number; stale: number; outdated: number }
  matter_step_result:  { total: number; stale: number; outdated: number }
  steps: Array<{
    step_name: string
    result_version_id: number | null
    production_version_id: number | null
    is_outdated: boolean
    is_stale: boolean
    result_kind: 'computation_result' | 'matter_step_result'
  }>
}

export async function fetchArtifactResultDrift(ids: number[]): Promise<{ by_artifact: Record<string, ArtifactDriftSummary> }> {
  if (ids.length === 0) return { by_artifact: {} }
  const r = await api.get(`/artifacts/result-drift/?ids=${ids.join(',')}`)
  return r.data
}

// Resolve composite_ids the AI mentioned in chat prose to clickable
// links. Privacy-respecting on the backend — codes the user can't see
// drop out of the result map silently.
export type CompositeResolution = {
  kind: 'matter' | 'artifact'
  id: number
  name?: string
}
export async function resolveCompositeIds(codes: string[]): Promise<{ resolutions: Record<string, CompositeResolution> }> {
  if (codes.length === 0) return { resolutions: {} }
  await ensureCsrf()
  const r = await api.post('/chat/lookup-composite/', { codes })
  return r.data
}

export async function runPipeline(artifactId: number, opts: { force?: boolean; sync?: boolean } = {}): Promise<{ status: string; run_id?: number }> {
  await ensureCsrf()
  const r = await api.post('/run-pipeline/', {
    artifact_id: artifactId,
    force: opts.force ?? false,
    async: !opts.sync,
  })
  return r.data
}

export async function runMatterPipeline(matterId: number, opts: { force?: boolean; sync?: boolean } = {}): Promise<{ status: string; run_id?: number; detail?: string }> {
  await ensureCsrf()
  const r = await api.post(`/run-matter-pipeline/${matterId}/`, {
    force: opts.force ?? false,
    async: !opts.sync,
  })
  return r.data
}

export async function runPipelineBulk(artifactIds: number[], opts: { force?: boolean } = {}): Promise<{ enqueued: number; rejected: number }> {
  await ensureCsrf()
  const r = await api.post('/run-pipeline-bulk/', {
    artifact_ids: artifactIds,
    force: opts.force ?? false,
  })
  return r.data
}

export type ImportSource = 'emails' | 'docs' | 'attachments'
export type ImportReport = Record<ImportSource, { created: number; updated: number; skipped: number; total: number } | undefined>

export async function importFromV1(opts: {
  client_code: string
  legacy_client_id: number
  sources: ImportSource[]
  limit?: number
  offset?: number
  /** ISO date (YYYY-MM-DD) or full datetime; only import rows with
   *  internal_date / modified_time >= this. Mirrors the CLI flag. */
  since?: string | null
  /** Same shape as `since`, but the upper bound (exclusive). */
  until?: string | null
}): Promise<{ client: string; legacy_client_id: number; limit: number; offset: number;
              since: string | null; until: string | null; report: ImportReport }> {
  await ensureCsrf()
  const r = await api.post('/imports/v1/', opts)
  return r.data
}

/** Per-matter result row inside a per_open_matter fanout response. */
export type PipelineTestMatterAssessment = {
  matter_id: number
  matter_code: string  // e.g. "M-013"
  matter_name: string
  matter_status: string
  matter_stage: string
  rendered_input: string
  output_parsed: unknown
  output_text: string
  input_tokens: number
  output_tokens: number
  latency_ms: number
  status: 'success' | 'error'
  error_message: string
}

export type PipelineTestStepResponse = {
  step_name: string
  slot_name: string
  prompt_version: { id: number; version: number; labels: string[]; is_pinned: boolean }
  rendered_input?: string
  output_parsed: unknown
  output_text?: string
  input_tokens?: number
  output_tokens?: number
  latency_ms?: number
  model: string
  // Skip-applicability marker for non-applicable artifact/step combos
  // (e.g. evaluate_image_content on a non-image artifact). Set by the
  // backend; UI shows a "skipped" badge instead of an output block.
  skipped?: boolean
  skip_reason?: string
  // True when L5+ step ran against an in-memory virtual matter
  // synthesized for L4's create_new path (matter is NOT in the DB).
  // UI shows a "virtual matter (not saved)" badge.
  virtual_matter?: boolean
  // Per_open_matter fanout fields. Present only when the step's
  // fanout_kind is 'per_open_matter' (currently L3 assess_matter_match).
  fanout?: boolean
  matter_assessments?: PipelineTestMatterAssessment[]
  summary?: {
    matters_evaluated: number
    does_belong_true: number
    errors: number
    total_latency_ms: number
  }
}

export async function pipelineTestStep(opts: {
  artifact_id: number
  step_id: number
  upstream_outputs?: Record<string, unknown>
  prompt_version_id?: number
}): Promise<PipelineTestStepResponse> {
  await ensureCsrf()
  const r = await api.post('/pipeline-test/step/', opts)
  return r.data
}

export async function runPipelineForMatter(matterId: number, opts: { force?: boolean } = {}): Promise<{ enqueued: number; matter_id: number }> {
  await ensureCsrf()
  const r = await api.post(`/matters/${matterId}/run-pipeline/`, {
    force: opts.force ?? false,
  })
  return r.data
}

export async function runStale(): Promise<{ enqueued: number }> {
  await ensureCsrf()
  const r = await api.post('/run-stale/', {})
  return r.data
}

export async function dagValidate(): Promise<{ ok: boolean; error?: string; levels?: Record<string, number>; step_count?: number }> {
  const r = await api.get('/dag-validate/')
  return r.data
}

// ─── Chat ────────────────────────────────────────────────────────────────────

export type ChatScopeKind = 'global' | 'matter' | 'artifact'

export type ChatToolCall = {
  name: string
  args: Record<string, unknown>
  result_preview: string
}

export type ChatPersistedMessage = {
  id: number
  role: 'user' | 'assistant' | 'tool'
  content: string
  tool_calls: ChatToolCall[] | null
  tool_call_id: string | null
  tool_name: string | null
  iterations: number | null
  input_tokens: number | null
  output_tokens: number | null
  latency_ms: number | null
  created_at: string
}

export type ChatContextMeter = {
  budget: number
  used_estimated: number
  used_reported: number
  trimmed: boolean
  dropped_count: number
  summarized_this_turn?: boolean
  has_summary?: boolean
  summary?: string
}

export type ChatHistoryResponse = {
  scope_kind: ChatScopeKind
  scope_id: number | null
  conversation_id: number | null
  messages: ChatPersistedMessage[]
  context: ChatContextMeter
}

export type ChatTurnResponse = {
  conversation_id: number
  scope_kind: ChatScopeKind
  scope_id: number | null
  user_message: ChatPersistedMessage
  assistant_message: ChatPersistedMessage
  iterations: number
  input_tokens: number
  output_tokens: number
  context: ChatContextMeter
}

export async function loadChatHistory(opts: {
  scope_kind?: ChatScopeKind
  scope_id?: number | null
}): Promise<ChatHistoryResponse> {
  const params: Record<string, string> = {}
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    params.scope_kind = opts.scope_kind
    if (opts.scope_id != null) params.scope_id = String(opts.scope_id)
  }
  const r = await api.get('/chat/history/', { params })
  return r.data
}

export async function clearChatHistory(opts: {
  scope_kind?: ChatScopeKind
  scope_id?: number | null
}): Promise<{ cleared: boolean }> {
  await ensureCsrf()
  const params: Record<string, string> = {}
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    params.scope_kind = opts.scope_kind
    if (opts.scope_id != null) params.scope_id = String(opts.scope_id)
  }
  const r = await api.delete('/chat/history/', { params })
  return r.data
}

export async function sendChat(opts: {
  message: string
  scope_kind?: ChatScopeKind
  scope_id?: number | null
  /** The artifact the user has selected in the UI right now. Sent
   *  separately from scope so a matter-scoped chat can know which
   *  artifact "this" refers to. */
  current_artifact_id?: number | null
}): Promise<ChatTurnResponse> {
  await ensureCsrf()
  const body: Record<string, unknown> = { message: opts.message }
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    body.scope_kind = opts.scope_kind
    if (opts.scope_id != null) body.scope_id = opts.scope_id
  }
  if (opts.current_artifact_id != null) body.current_artifact_id = opts.current_artifact_id
  const r = await api.post('/chat/', body)
  return r.data
}

// ─── Streaming chat ──────────────────────────────────────────────────────────

export type ChatStreamEvent =
  | { type: 'chunk'; text: string }
  | { type: 'tool_call'; name: string; args: Record<string, unknown>; tool_call_id: string }
  | { type: 'tool_result'; name: string; preview: string; tool_call_id: string }
  | { type: 'done'; payload: ChatStreamDonePayload }
  | { type: 'error'; detail: string }

export type ChatStreamDonePayload = {
  conversation_id: number
  scope_kind: ChatScopeKind
  scope_id: number | null
  user_message_id: number
  assistant_message_id: number | null
  iterations: number
  input_tokens: number
  output_tokens: number
  context: ChatContextMeter
}

/**
 * Stream a chat turn from the server. Returns an async generator of
 * typed events. Backend pushes SSE frames (event: chunk/tool_call/...);
 * we parse them into a discriminated union the consumer can switch on.
 *
 * Usage:
 *   for await (const ev of sendChatStream({...})) {
 *     if (ev.type === 'chunk') append(ev.text)
 *     else if (ev.type === 'tool_call') showStatus(`calling ${ev.name}...`)
 *     else if (ev.type === 'done') invalidateHistory()
 *   }
 */
export async function* sendChatStream(opts: {
  message: string
  scope_kind?: ChatScopeKind
  scope_id?: number | null
  current_artifact_id?: number | null
  signal?: AbortSignal
}): AsyncGenerator<ChatStreamEvent, void, unknown> {
  await ensureCsrf()
  const body: Record<string, unknown> = { message: opts.message }
  if (opts.scope_kind && opts.scope_kind !== 'global') {
    body.scope_kind = opts.scope_kind
    if (opts.scope_id != null) body.scope_id = opts.scope_id
  }
  if (opts.current_artifact_id != null) body.current_artifact_id = opts.current_artifact_id

  // Get CSRF token so the POST passes Django middleware.
  const csrfToken = readCookie('csrftoken') || ''

  const resp = await fetch(`${API_BASE}/chat/stream/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: opts.signal,
  })
  if (!resp.ok || !resp.body) {
    let detail = `${resp.status} ${resp.statusText}`
    try {
      const j = await resp.json()
      if (j?.detail) detail = j.detail
    } catch { /* not json */ }
    yield { type: 'error', detail }
    return
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    // SSE frames are separated by \n\n.
    let frameEnd = buf.indexOf('\n\n')
    while (frameEnd >= 0) {
      const frame = buf.slice(0, frameEnd)
      buf = buf.slice(frameEnd + 2)
      const ev = parseSseFrame(frame)
      if (ev) yield ev
      frameEnd = buf.indexOf('\n\n')
    }
  }
}

function parseSseFrame(frame: string): ChatStreamEvent | null {
  let event = ''
  const dataLines: string[] = []
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
  }
  if (!event || dataLines.length === 0) return null
  let data: unknown
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }
  const d = data as Record<string, unknown>
  switch (event) {
    case 'chunk':
      return { type: 'chunk', text: String(d.text ?? '') }
    case 'tool_call':
      return {
        type: 'tool_call',
        name: String(d.name ?? ''),
        args: (d.args as Record<string, unknown>) ?? {},
        tool_call_id: String(d.tool_call_id ?? ''),
      }
    case 'tool_result':
      return {
        type: 'tool_result',
        name: String(d.name ?? ''),
        preview: String(d.preview ?? ''),
        tool_call_id: String(d.tool_call_id ?? ''),
      }
    case 'done':
      return { type: 'done', payload: d as unknown as ChatStreamDonePayload }
    case 'error':
      return { type: 'error', detail: String(d.detail ?? 'stream error') }
    default:
      return null
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`))
  return m ? decodeURIComponent(m[2]) : null
}

// ─── Per-matter assessments (assess_matter_match results) ───────────────────

export type MatchAssessment = {
  matter_id: number
  matter_client_seq: number
  matter_name: string
  matter_status: string
  matter_stage: string
  does_belong: boolean | null
  confidence: number | null
  primary: boolean
  reason: string
  lifecycle_signal: string | null
  evidence_quote: string
  status: string
  computed_at: string
}

export async function getMatchAssessments(artifactId: number): Promise<{ count: number; assessments: MatchAssessment[] }> {
  const r = await api.get(`/artifacts/${artifactId}/match-assessments/`)
  return r.data
}

// ─── Matter step results (L4/L5 fanout outputs) ─────────────────────────────

export type MatterStepResult = {
  step_id: number
  step_name: string
  output: Record<string, unknown>
  status: string
  is_stale: boolean
  triggered_by_artifact_id: number | null
  prompt_version: number | null
  latency_ms: number
  computed_at: string
}

export async function getMatterStepResults(matterId: number): Promise<{ matter_id: number; results: MatterStepResult[] }> {
  const r = await api.get(`/matters/${matterId}/step-results/`)
  return r.data
}
